import { useEffect, useRef, useSyncExternalStore } from 'react';
import { playSfx, startMusic, stopMusic, claimMusic, isMuted, subscribeMuted } from './sfx';
import type { RushMidSnapshot } from './useRushMidMatch';

/** Subscribe to the RushMid mute flag (persisted). SSR falls back to unmuted. */
export function useRushMidMuted(): boolean {
  return useSyncExternalStore(subscribeMuted, isMuted, () => false);
}

/**
 * Rush Mid audio lifecycle, driven by the current phase: a one-shot stinger when a match is
 * found (entering BUYING), and the menu theme on the loading screen / lobby / while searching
 * (stopped once a match is found, restarted on return to the lobby). Muting stops the music
 * at once and blocks every sfx; unmuting in the lobby brings the theme back.
 */
export function useRushMidAudio(
  assetsReady: boolean,
  status: RushMidSnapshot['status'],
  partyMembers: number | null,
): void {
  const muted = useRushMidMuted();
  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (!assetsReady) return;
    if (status === 'BUYING' && prevStatusRef.current !== 'BUYING') playSfx('match_found');
    // the fight kicks off (buy window closed) → epic transition sting, once per match
    if (status === 'PLAYING' && prevStatusRef.current !== 'PLAYING') playSfx('battle_start', 0.8);
    prevStatusRef.current = status;
  }, [assetsReady, status]);

  // a blip whenever the party grows — a friend joined by code. Baseline resets on leave
  // (null), so re-entering a party or joining yourself never false-fires.
  const prevMembersRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevMembersRef.current;
    prevMembersRef.current = partyMembers;
    if (assetsReady && prev != null && partyMembers != null && partyMembers > prev) playSfx('party_join', 0.6);
  }, [assetsReady, partyMembers]);

  useEffect(() => {
    // Menu theme plays across the whole lobby: loading, the plain lobby (NONE), the party
    // lobby (PARTY) and while seated in an open game waiting for it to fill (OPEN). It stops
    // once the match starts (BUYING+). Missing PARTY here silenced the music in the party lobby.
    const inLobby = status === 'NONE' || status === 'PARTY' || status === 'OPEN';
    if (!muted && (!assetsReady || inLobby)) void startMusic();
    else stopMusic();
  }, [assetsReady, status, muted]);

  // Unmount releases a CLAIM instead of stopping outright: navigating to another RushMid
  // stack screen (match detail) claims the theme too, so the track survives the transition;
  // leaving the section entirely drops the last claim and the music stops after a beat.
  useEffect(() => claimMusic(), []);
}
